/**
 * Minimal, SAFE Markdown renderer for the tutor's responses.
 *
 * There's no markdown dependency in the repo and we don't introduce one: we render
 * to SolidJS nodes (never `innerHTML`), so the model's text CANNOT
 * inject HTML/JS. Supports what the tutor produces: fenced code blocks with
 * ``` ``` ```, inline code `` `x` ``, **bold**, `- ` lists and
 * paragraphs. Style aligned with the help primitives (subtle text, monospace).
 */
import { For, type JSX, Match, Switch } from "solid-js";

type Block =
  | { type: "code"; lang: string; code: string }
  | { type: "ul"; items: string[] }
  | { type: "p"; text: string };

/** Splits the text into blocks (fenced code, lists, paragraphs). PURE. */
export function parseBlocks(input: string): Block[] {
  const blocks: Block[] = [];
  const lines = input.replaceAll("\r\n", "\n").split("\n");
  let i = 0;
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: "p", text: paragraph.join(" ").trim() });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list.length > 0) {
      blocks.push({ type: "ul", items: [...list] });
      list = [];
    }
  };

  while (i < lines.length) {
    const line = lines[i] ?? "";
    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      flushParagraph();
      flushList();
      const lang = fence[1] ?? "";
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i] ?? "")) {
        code.push(lines[i] ?? "");
        i += 1;
      }
      i += 1; // consume cierre
      blocks.push({ type: "code", lang, code: code.join("\n") });
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      flushParagraph();
      list.push(line.replace(/^\s*[-*]\s+/, ""));
      i += 1;
      continue;
    }
    if (line.trim() === "") {
      flushParagraph();
      flushList();
      i += 1;
      continue;
    }
    flushList();
    paragraph.push(line.trim());
    i += 1;
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Renders inline formatting (**bold** and `code`) into safe nodes. */
function inline(text: string): JSX.Element {
  const parts: JSX.Element[] = [];
  // Splits alternating on `code` and **bold** without using innerHTML.
  const regex = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith("`")) {
      parts.push(
        <code class="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      parts.push(<strong class="font-semibold">{token.slice(2, -2)}</strong>);
    }
    last = match.index + token.length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return <>{parts}</>;
}

// Narrowing the discriminated union WITHOUT assertions (`as`): each helper
// returns the typed block or undefined for <Match>'s keyed `when`.
const codeOf = (b: Block) => (b.type === "code" ? b : undefined);
const ulOf = (b: Block) => (b.type === "ul" ? b : undefined);
const pOf = (b: Block) => (b.type === "p" ? b : undefined);

export function TutorMarkdown(props: { text: string }): JSX.Element {
  const blocks = () => parseBlocks(props.text);
  return (
    <div class="space-y-2 text-sm leading-relaxed">
      <For each={blocks()}>
        {(block) => (
          <Switch>
            <Match when={codeOf(block)} keyed>
              {(code) => (
                <pre class="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
                  <code>{code.code}</code>
                </pre>
              )}
            </Match>
            <Match when={ulOf(block)} keyed>
              {(ul) => (
                <ul class="list-disc space-y-1 pl-5">
                  <For each={ul.items}>{(item) => <li>{inline(item)}</li>}</For>
                </ul>
              )}
            </Match>
            <Match when={pOf(block)} keyed>
              {(p) => <p>{inline(p.text)}</p>}
            </Match>
          </Switch>
        )}
      </For>
    </div>
  );
}
